import React from 'react';
import styled from 'styled-components';

import Button from '../../../../../Button';
import type {
  CallSummary as CallSummaryData,
  FinalCallSummary,
} from '../../../../Calls/types';

import {
  formatConfidence,
  formatGeneratedAt,
  formatScore,
  getEvidenceQuotes,
  getText,
  getTextItems,
} from './utils';

interface Props {
  callId?: string | null;
  finalCallSummary: FinalCallSummary;
  isExpanded: boolean;
  onToggle(): void;
}

const SummaryList = ({ items }: { items: string[] }) =>
  items.length ? (
    <StyledList>
      {items.map((item, index) => (
        <li key={`${index}-${item}`}>{item}</li>
      ))}
    </StyledList>
  ) : (
    <StyledPlaceholder>—</StyledPlaceholder>
  );

const DetailSection = ({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) => (
  <StyledDetailSection>
    <StyledDetailTitle>{title}</StyledDetailTitle>
    {children}
  </StyledDetailSection>
);

const CallSummary = ({
  callId,
  finalCallSummary,
  isExpanded,
  onToggle,
}: Props) => {
  const { generatedAt, status, summary } = finalCallSummary;

  if (status === 'pending') {
    return (
      <StyledCallSummary>
        <StyledTitle>AI-саммари</StyledTitle>
        <StyledStatus>Саммари формируется</StyledStatus>
      </StyledCallSummary>
    );
  }

  if (status === 'failed') {
    return (
      <StyledCallSummary>
        <StyledTitle>AI-саммари</StyledTitle>
        <StyledStatus>Не удалось сформировать саммари</StyledStatus>
      </StyledCallSummary>
    );
  }

  if (
    status !== 'completed' ||
    !summary ||
    typeof summary !== 'object' ||
    Array.isArray(summary)
  ) {
    return null;
  }

  const summaryData = summary as CallSummaryData;
  const clientReason = getText(summaryData.clientReason);
  const conversationOutcome = getText(summaryData.conversationOutcome);
  const nextBestAction = getText(summaryData.nextBestAction);
  const whatWeDidRight = getTextItems(summaryData.whatWeDidRight);
  const whatWeDidWrong = getTextItems(summaryData.whatWeDidWrong);
  const sentiment = getText(summaryData.sentiment);
  const serviceScore = formatScore(
    summaryData.serviceDissatisfactionScore,
    summaryData.serviceDissatisfactionLabel
  );
  const serviceReason = getText(summaryData.serviceDissatisfactionReason);
  const outcomeScore = formatScore(
    summaryData.outcomeDissatisfactionScore,
    summaryData.outcomeDissatisfactionLabel
  );
  const outcomeReason = getText(summaryData.outcomeDissatisfactionReason);
  const evidenceQuotes = getEvidenceQuotes(summaryData.evidence);
  const confidence = formatConfidence(summaryData.confidence);
  const generatedAtText = formatGeneratedAt(generatedAt);
  const hasCompactSummary = Boolean(
    clientReason || conversationOutcome || nextBestAction
  );
  const hasDetails = Boolean(
    whatWeDidRight.length ||
      whatWeDidWrong.length ||
      sentiment ||
      serviceScore ||
      serviceReason ||
      outcomeScore ||
      outcomeReason ||
      evidenceQuotes.length ||
      confidence ||
      generatedAtText
  );

  if (!hasCompactSummary && !hasDetails) {
    return null;
  }

  const detailsId =
    callId !== undefined ? `call-summary-details-${callId}` : undefined;

  return (
    <StyledCallSummary>
      <StyledTitle>AI-саммари</StyledTitle>
      {clientReason && (
        <StyledCompactRow>
          <StyledLabel>Причина обращения:</StyledLabel>{' '}
          <StyledValue>{clientReason}</StyledValue>
        </StyledCompactRow>
      )}
      {conversationOutcome && (
        <StyledCompactRow>
          <StyledLabel>Итог разговора:</StyledLabel>{' '}
          <StyledValue>{conversationOutcome}</StyledValue>
        </StyledCompactRow>
      )}
      {nextBestAction && (
        <StyledCompactRow>
          <StyledLabel>Следующий шаг:</StyledLabel>{' '}
          <StyledValue>{nextBestAction}</StyledValue>
        </StyledCompactRow>
      )}
      {hasDetails && (
        <StyledToggle
          aria-controls={detailsId}
          aria-expanded={isExpanded}
          functional
          onClick={onToggle}
          type="button"
        >
          {isExpanded ? 'Свернуть' : 'Подробнее'}
        </StyledToggle>
      )}
      {hasDetails && isExpanded && (
        <StyledDetails id={detailsId}>
          <DetailSection title="Что сделали хорошо">
            <SummaryList items={whatWeDidRight} />
          </DetailSection>
          <DetailSection title="Что сделали плохо">
            <SummaryList items={whatWeDidWrong} />
          </DetailSection>
          <DetailSection title="Настроение клиента">
            <StyledDetailText>{sentiment || '—'}</StyledDetailText>
          </DetailSection>
          <DetailSection title="Недовольство нашей работой">
            <StyledDetailText>
              <StyledLabel>Оценка:</StyledLabel>{' '}
              <StyledValue>{serviceScore ?? '—'}</StyledValue>
            </StyledDetailText>
            <StyledDetailText>
              <StyledLabel>Причина:</StyledLabel>{' '}
              <StyledValue>{serviceReason || '—'}</StyledValue>
            </StyledDetailText>
          </DetailSection>
          <DetailSection title="Недовольство исходом ситуации">
            <StyledDetailText>
              <StyledLabel>Оценка:</StyledLabel>{' '}
              <StyledValue>{outcomeScore ?? '—'}</StyledValue>
            </StyledDetailText>
            <StyledDetailText>
              <StyledLabel>Причина:</StyledLabel>{' '}
              <StyledValue>{outcomeReason || '—'}</StyledValue>
            </StyledDetailText>
          </DetailSection>
          <DetailSection title="Цитаты из разговора">
            {evidenceQuotes.length ? (
              <StyledQuotes>
                {evidenceQuotes.map((quote, index) => (
                  <StyledQuote key={`${index}-${quote}`}>{quote}</StyledQuote>
                ))}
              </StyledQuotes>
            ) : (
              <StyledPlaceholder>—</StyledPlaceholder>
            )}
          </DetailSection>
          <StyledMeta>
            <StyledLabel>Уверенность:</StyledLabel>{' '}
            <StyledValue>{confidence || '—'}</StyledValue>
          </StyledMeta>
          <StyledMeta>
            <StyledLabel>Сформировано:</StyledLabel>{' '}
            <StyledValue>{generatedAtText || '—'}</StyledValue>
          </StyledMeta>
        </StyledDetails>
      )}
    </StyledCallSummary>
  );
};

const StyledCallSummary = styled.div`
  margin-top: 10px;
`;

const StyledTitle = styled.div`
  font-weight: 600;
`;

const StyledStatus = styled.div`
  margin-top: 5px;
`;

const StyledCompactRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 6px;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

const StyledLabel = styled.span`
  flex-shrink: 0;
  font-weight: 600;
`;

const StyledValue = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

const StyledToggle = styled(Button)`
  color: #007aff;
  display: block;
  margin-top: 8px;
  padding: 0;

  &:hover {
    color: #179fe3;
  }
`;

const StyledDetails = styled.div`
  border-top: 1px solid #e6e6e6;
  margin-top: 8px;
  padding-top: 8px;
`;

const StyledDetailSection = styled.div`
  & + & {
    margin-top: 10px;
  }
`;

const StyledDetailTitle = styled.div`
  font-weight: 600;
`;

const StyledDetailText = styled.div`
  display: flex;
  margin-top: 4px;
  flex-direction: column;
`;

const StyledPlaceholder = styled.div`
  margin-top: 4px;
`;

const StyledList = styled.ul`
  margin: 4px 0 0;
  padding-left: 18px;
  list-style: auto;

  li {
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
`;

const StyledQuotes = styled.div`
  margin-top: 4px;
`;

const StyledQuote = styled.blockquote`
  border-left: 2px solid #c2c5cc;
  margin: 6px 0 0;
  overflow-wrap: anywhere;
  padding-left: 8px;
  white-space: pre-wrap;
`;

const StyledMeta = styled.div`
  color: #919499;
  display: flex;
  font-size: 12px;
  margin-top: 8px;
  gap: 4px;
`;

export default CallSummary;
